import 'vue/jsx'; // this fixes the problem with untyped emits!
import '@/extensions';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
import { createApp } from 'vue';
import { router } from '@/router';
import { LicenseManager } from 'ag-grid-enterprise';
import { appSettings } from '@/appSettings';
import { scrollBarVisible } from '@/directives';
import { i18n } from '@/i18n';
import { calendarLocales } from '@/i18n/dateFormats';
import { pinia } from '@/store';

import App from '@/App.vue';
import VCalendar from 'v-calendar';
import FloatingVue from 'floating-vue';
import Icon from '@/components/common/Icon.vue';
import VAlert from '@/components/VAlert.vue';
import VueSanitize from 'vue-sanitize-directive';
import {
    installCoreComponents,
    installFormComponents,
    installChartComponents,
    installDirectives,
} from '@sysarb/shared-style';
import { QueryClient, VueQueryPlugin } from '@tanstack/vue-query';
//Ag-Grid
import AgDataTableProgressBar from './components/ag-grid/widgets/AgDataTableProgressBar.vue';
import AgStatusWidget from '@/components/ag-grid/widgets/AgStatusWidget.vue';
import AgIcon from '@/components/ag-grid/widgets/AgIcon.vue';
import AgSync from '@/components/ag-grid/widgets/AgSync.vue';
import GridLoader from '@/components/ag-grid/GridLoader.vue';
import AgCheckbox from '@/components/ag-grid/widgets/AgCheckbox.vue';
import AgTextEditor from '@/components/ag-grid/widgets/AgTextEditor.vue';
import AgManagerSelect from '@/components/ag-grid/widgets/AgManagerSelect.vue';
import AgEmploymentTooltip from '@/components/ag-grid/widgets/AgEmploymentTooltip.vue';
import AgListTooltip from '@/components/ag-grid/widgets/AgListTooltip.vue';
import AgItalicValue from '@/components/ag-grid/widgets/AgItalicValue.vue';
import AgButton from '@/components/ag-grid/widgets/AgButton.vue';
import AgButtonIcon from '@/components/ag-grid/widgets/AgButtonIcon.vue';
import AgOptionSelect from '@/components/ag-grid/widgets/AgOptionSelect.vue';
import AgCircularProgress from '@/components/ag-grid/widgets/AgCircularProgress.vue';
import AgCompoundValue from '@/components/ag-grid/widgets/AgCompoundValue.vue';
import AgToggleHeader from '@/components/ag-grid/widgets/AgToggleHeader.vue';
import AgHeaderWithInfo from '@/components/ag-grid/widgets/AgHeaderWithInfo.vue';
import AgLoading from '@/components/ag-grid/widgets/AgLoading.vue';
import AgBadge from '@/components/ag-grid/widgets/AgBadge.vue';
import AgEditField from '@/components/ag-grid/widgets/AgEditField.vue';
import AgEditFieldBadge from '@/components/ag-grid/widgets/AgEditFieldBadge.vue';
import AgMappingWorkTags from '@/components/ag-grid/widgets/AgMappingWorkTags.vue';
import AgGroupBadge from '@/components/ag-grid/widgets/AgGroupBadge.vue';
import AgSimpleTextIcon from '@/components/ag-grid/widgets/AgSimpleTextIcon.vue';
import AgRouterLink from '@/components/ag-grid/widgets/AgRouterLink.vue';
import AgDropdownEditor from '@/components/ag-grid/widgets/AgDropdownEditor.vue';
import AgSDropdownEditor from '@/components/ag-grid/widgets/AgSDropdownEditor.vue';
import AgTreeNodeEditor from '@/components/ag-grid/widgets/AgTreeNodeEditor.vue';
import AgNumberEditor from '@/components/ag-grid/widgets/AgNumberEditor.vue';
import AgTreeNodeSelect from '@/components/ag-grid/widgets/AgTreeNodeSelect.vue';
import AgTitleWithBadge from '@/components/ag-grid/widgets/AgTitleWithBadge.vue';
import AgIntegrationIcon from '@/components/ag-grid/widgets/AgIntegrationIcon.vue';

// Highcharts
import Highcharts from 'highcharts';
import InitExporting from 'highcharts/modules/exporting';
import HighchartsAccessibility from 'highcharts/modules/accessibility';
import NoData from 'highcharts/modules/no-data-to-display';
import Map from 'highcharts/modules/map';
import Boost from 'highcharts/modules/boost';
import HighchartsMore from 'highcharts/highcharts-more';
import DraggablePoints from 'highcharts/modules/draggable-points';
import ExportLocal from 'highcharts/modules/offline-exporting';
import HighchartsCustomEvents from 'highcharts-custom-events';
import HighchartsAnnotation from 'highcharts/modules/annotations';
import { defaultOptions } from '@/common/chartHelper/ChartDefaultOptions';
import '@/proj4';
import { createGtm } from '@gtm-support/vue-gtm';
import { EventService } from '@/services/EventService';
import { RightsService } from '@/services/RightsService';
import { useConsoleLogger } from '@/composables/useConsoleLogger';
import { api } from './api';
import AgDiscussionsEmploymentsStatusBar from './components/ag-grid/widgets/AgDiscussionsEmploymentsStatusBar.vue';

Map(Highcharts);
NoData(Highcharts);
InitExporting(Highcharts);
HighchartsMore(Highcharts);
DraggablePoints(Highcharts);
ExportLocal(Highcharts);
HighchartsCustomEvents(Highcharts);
HighchartsAccessibility(Highcharts);
HighchartsAnnotation(Highcharts);
Boost(Highcharts);

Highcharts.setOptions(defaultOptions);

LicenseManager.setLicenseKey(appSettings.agGridKey);

/** Instance settings */
const app = createApp(App);

// Initialize enhanced console logger BEFORE GTM to intercept GTM messages
if (import.meta.env.DEV) {
    const { initConsoleLogger } = useConsoleLogger();
    initConsoleLogger();
} else {
    console.info('%c╔═╗╦ ╦╔═╗╔═╗╦═╗╔═╗ ', 'color: #a78230');
    console.info('%c╚═╗╚╦╝╚═╗╠═╣╠╦╝╠═╩╗', 'color: #a78230');
    console.info('%c╚═╝ ╩ ╚═╝╩ ╩╩╚═╚══╝', 'color: #a78230');
}

/** Install components */
installCoreComponents(app);
installFormComponents(app);
installChartComponents(app);
installDirectives(app);

/** Providers */
app.provide('eventService', EventService.getInstance(router));
app.provide('rightsService', RightsService.getInstance());

/** Global Components */
app.component('Icon', Icon);
app.component('VAlert', VAlert);
app.component('AgDataTableProgressBar', AgDataTableProgressBar);
app.component('AgStatusWidget', AgStatusWidget);
app.component('AgIcon', AgIcon);
app.component('AgIntegrationIcon', AgIntegrationIcon);
app.component('AgSync', AgSync);
app.component('GridLoader', GridLoader);
app.component('AgCheckbox', AgCheckbox);
app.component('AgTextEditor', AgTextEditor);
app.component('AgManagerSelect', AgManagerSelect);
app.component('AgEmploymentTooltip', AgEmploymentTooltip);
app.component('AgListTooltip', AgListTooltip);
app.component('AgItalicValue', AgItalicValue);
app.component('AgButton', AgButton);
app.component('AgOptionSelect', AgOptionSelect);
app.component('AgCircularProgress', AgCircularProgress);
app.component('AgButtonIcon', AgButtonIcon);
app.component('AgCompoundValue', AgCompoundValue);
app.component('AgToggleHeader', AgToggleHeader);
app.component('AgHeaderWithInfo', AgHeaderWithInfo);
app.component('AgLoading', AgLoading);
app.component('AgMappingWorkTags', AgMappingWorkTags);
app.component('AgBadge', AgBadge);
app.component('AgEditFieldBadge', AgEditFieldBadge);
app.component('AgEditField', AgEditField);
app.component('AgGroupBadge', AgGroupBadge);
app.component('AgSimpleTextIcon', AgSimpleTextIcon);
app.component('AgDiscussionsEmploymentsStatusBar', AgDiscussionsEmploymentsStatusBar);
app.component('AgRouterLink', AgRouterLink);
app.component('AgDropdownEditor', AgDropdownEditor);
app.component('AgSDropdownEditor', AgSDropdownEditor);
app.component('AgTreeNodeEditor', AgTreeNodeEditor);
app.component('AgNumberEditor', AgNumberEditor);
app.component('AgTreeNodeSelect', AgTreeNodeSelect);
app.component('AgTitleWithBadge', AgTitleWithBadge);
/** Directives */
app.directive('scroll-bar-visible', scrollBarVisible);

/** Use plugins */
app.use(pinia);
app.use(VueQueryPlugin);
app.use(i18n);
app.use(VCalendar, { locales: calendarLocales });
app.use(router);
app.use(FloatingVue, {
    themes: {
        'dark-tooltip': {
            $extend: 'tooltip',
        },
        'tooltip': {
            distance: 8,
            html: true
        },
        'dropdown': {
            distance: 8,
            overflowPadding: 5
        }
    }
});

app.use(createGtm({
    id: appSettings.analyticsKey, // Your GTM single container ID, array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy'] or array of objects [{id: 'GTM-xxxxxx', queryParams: { gtm_auth: 'abc123', gtm_preview: 'env-4', gtm_cookies_win: 'x'}}, {id: 'GTM-yyyyyy', queryParams: {gtm_auth: 'abc234', gtm_preview: 'env-5', gtm_cookies_win: 'x'}}], // Your GTM single container ID or array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy']
    // queryParams: {
    //     // Add URL query string when loading gtm.js with GTM ID (required when using custom environments)
    //     gtm_auth: appSettings.analyticsKey,
    //     gtm_preview: 'env-4',
    //     gtm_cookies_win: 'x',
    // },
    // source: 'https://customurl.com/gtm.js', // Add your own serverside GTM script
    // defer: false, // Script can be set to `defer` to speed up page load at the cost of less accurate results (in case visitor leaves before script is loaded, which is unlikely but possible). Defaults to false, so the script is loaded `async` by default
    // compatibility: false, // Will add `async` and `defer` to the script tag to not block requests for old browsers that do not support `async`
    // nonce: '2726c7f26c', // Will add `nonce` to the script tag
    // Start disabled so the GTM/Google Analytics script is NOT injected on app boot.
    // The script must only load AFTER the user grants statistics cookie consent via Cookiebot.
    // Consent is handled in App.vue: when cookieBot.statistics becomes true it calls
    // useAnalytics().init() -> gtm.enable(true), which injects the script at that point
    // (enable(true) loads the script when loadScript is true and it isn't already present).
    enabled: false,
    debug: true, // Whether or not display console logs debugs (optional)
    loadScript: appSettings.enableGtm, // Whether or not to load the GTM Script (Helpful if you are including GTM manually, but need the dataLayer functionality in your components) (optional)
    vueRouter: router, // Pass the router instance to automatically sync with router (optional)
    // ignoredViews: ['homepage'], // Don't trigger events for specified router names (optional)
    trackOnNextTick: false, // Whether or not call trackView in Vue.nextTick
}));
app.use(VueSanitize);

/** Mount */
app.mount('#app');

// Add logout function to window object so we can force logout from the devtools console
window.logout = (): void => {
    void new QueryClient().fetchQuery(api.login.queries.invalidate()).then(() => {
        void new QueryClient().fetchQuery(api.login.queries.validate(true));
    });
};
